import _ from 'lodash';
import { identity, isEqual, merge as lodashMerge } from 'lodash';
import { DateTime } from 'luxon'
import { EMPTY, from, iif, merge, Observable, of, ReplaySubject, Subject, Subscription, throwError, timer, zip } from 'rxjs';
import {
    catchError,
    concatMap,
    delayWhen,
    distinctUntilChanged,
    filter,
    first,
    last,
    map,
    mergeMap,
    pluck,
    retryWhen,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';

import { 
    SearchResponse, 
    Cache, 
    isSearchResponse, 
    MediaType, 
    AssociatedMedias, 
    DataSynchronisationStatus, 
    DataSynchroEvent, 
    SynchroMediaUpdates, 
    ReplaySubjectContent, 
    PushCachedValueResult,
    MEDIA_SUBJECTS_KEYS,
    DataStoreOptions,
    PushResult,
} from '@skinsoft/types'
import { userSelectors } from '@skinsoft/selectors'
import { fetch, DEFAULT_PROXY } from '@skinsoft/network'
import { AuthService } from '@skinsoft/auth/src/service'
import { OfflineDataFetcher, OfflineDataUpdateCacher, IndexedDBCache } from '@skinsoft/offline'
import { getCurrentDateAsSkinDate } from '@skinsoft/utils'
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'

type FragmentParser<T> = (response: any) => T[];

declare const FALLBACK_LOCALE;

export const dataSynchroEventBus$ = new Subject<DataSynchroEvent>();

const SEARCH_PAGE_SIZE: number = 100;

const MONGO_ID_REGEX = /^[a-f\d]{24}$/i

/**
 * Base class for all data management in the app.
 * Generics : 
 *  - T : principal type of the entity managed by this class
 *  - S : secondary type retrieved when performing a search query (if not specified, it is equals to T by default)
 */
export abstract class DataStore<T extends { id: string }, S extends { id: string } = T> {
    public userSelectors = userSelectors;

    protected synchronizationMap: {[key: string]: string} = {}

    public get sanitizedIdentifer() {
        // For a strange reason, 'replaceAll' doesn't exist in Chrome for now, so we cheat
        const sanitized = this.endpoint.split('/').join('');
        return sanitized
    }

    public get getNbEntitiesInUpdateCache() {
        return this.nbEntitiesInUpdateCache
    }

    public get getNbLocallyCreatedEntities() {
        return this.nbCreatedEntitiesInUpdateCache;
    }

    public get isSyncing() {
        return this.syncing;
    }

    private get mainCacheKey() {
        return this.endpoint;
    }

    public get lastSyncStats() {
        return this.offlineDataFetcher.getLastSynchroStats();
    }

    protected proxy = DEFAULT_PROXY;
    protected endpoint: string;
    protected cache: Cache<T>;

    //// OFFLINE //////////////////////////////////
    protected offlineDataFetcher: OfflineDataFetcher<T, S>;
    protected offlineDataUpdateCacher: OfflineDataUpdateCacher<T>;
    protected emulateSearch: boolean = false;

    // Stats
    private currentNbSychronized: number = 0;
    private maxNbToSynchronize: number = Infinity;
    private fragmentParser: FragmentParser<T>;
    private userGroupId: string;
    private typesForSearch: string[]
    private typesForSyncDown: string[]

    // Offline mode 
    private globalPushing = false;

    private dataStore: DataStore<T>;
    private syncing: boolean = false;
    private pushing: boolean = false;
    private pushresult: PushResult = { nbPushSuccesses: 0, nbToPush: 0 };
    private nbEntitiesInUpdateCache: number = 0;
    private nbCreatedEntitiesInUpdateCache: number = 0;

    private lastSynchroStats: any = null;
    private lastPushDate: number = 0;


    private executionStartTime: DateTime | null = null;

    private currentSynchroSubscription: Subscription = null as any;

    private abortSync: boolean = false;

    constructor(
        endpoint: string,
        typesForSearch?: string[],
        typesForSyncDown?: string[],
        fragmentParser: FragmentParser<T> = identity,
        proxy = DEFAULT_PROXY,
        cache: Cache<T> = new IndexedDBCache<T>(),
    ) {
        this.fragmentParser = fragmentParser;
        this.endpoint = endpoint;
        this.cache = cache;
        this.proxy = proxy;
        if (typesForSearch) {
            this.typesForSearch = typesForSearch;
            if(!typesForSyncDown) {
                this.typesForSyncDown = typesForSearch
            }
        }
        if(typesForSyncDown) {
            this.typesForSyncDown = typesForSyncDown
        }

        // Fetching user group
        AuthService.getInstance().user$.pipe(
            switchMap((user) => {
                if (user) {
                    return this.proxy.fetch("/groups/userGroup", {
                        userId: user.id,
                        workspace: (localStorage.getItem("workspace") || userSelectors.defaultWorkspace(user).id),
                    })
                } else {
                    return EMPTY;
                }
            })
        ).subscribe((group) => {
            this.userGroupId = group.id;
        });

        // Offline mode
        this.initializeDataFetcher();
        this.initializeDataPusher();

    }

    /**
     * @deprecated This method fetches all data of one type. This can lead to performance issues 
     * or crash if data is very numerous. The api endpoint will probably be removed in future.
     * @param options 
     * @param method 
     */
    public fetchAll(options: DataStoreOptions = {}, method = 'GET'): Observable<T[]> {

        const searchParams = new URLSearchParams(options.query);
        searchParams.set('archived', 'false');

        const endpoint = options.endpoint || this.endpoint;
        const cacheKey = `${endpoint}?${searchParams.toString()}`;

        return merge(
            this.proxy.fetch(endpoint, method === 'GET' ? searchParams : undefined, method, method !== 'GET' ? options.query : undefined).pipe(
                map(this.fragmentParser),
                // tap(data => this.cache.write(cacheKey, data)),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            ),
            // this.cache$(cacheKey),
        ).pipe(distinctUntilChanged(isEqual));
    }

    public fetchOne(id: string, networkFirst = false): Observable<T> {
        // if online
        return from(this.hasCachedUpdates(id)).pipe(
            switchMap(hasCachedUpdates => {
                if (window.navigator.onLine && !hasCachedUpdates) {
                    const uri = `${this.endpoint}/${id}`;
        
                    const network$ = this.proxy.fetch(uri, undefined, undefined, undefined, networkFirst).pipe(
                        last(),
                        tap(data => this.offlineDataFetcher.updateOne(data.id, data)),
                        catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
                    );
                    let source$ = network$;
        
                    if (networkFirst) {
                        source$ = source$.pipe(delayWhen(() => network$));
                    }
        
                    return source$.pipe(
                        distinctUntilChanged(isEqual),
                        this.mergeUpdateCacheDataToOneResult.bind(this),
                    );
                }
                // if offline
                else {
                    return this.fetchOneOfflineMode(id);
                }
            }),
        )
    }

    public fetchOneOnlineMode(id: string): Observable<T> {
        const uri = `${this.endpoint}/${id}`;
        return this.proxy.fetch(uri, undefined, undefined, undefined).pipe(
            last(),
            tap(data => this.offlineDataFetcher.updateOne(data.id, data)),
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        );
    }

    public fetchOneOfflineMode(id: string): Observable<T> {
        return from(this.hasCachedUpdates(id)).pipe(
            switchMap(hasCachedUpdates => {
                if(hasCachedUpdates) {
                    return this.offlineDataUpdateCacher.fetchOneFromId(id)
                }
                return this.offlineDataFetcher.fetchOneFromId(id)
            }),
            switchMap(async (data: any) => {
                // merging values from update-cache with value from fetch-cache not to loose information
                const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id);
                const theMerge = lodashMerge(await data, cachedUpdatedValue)
                return theMerge;
            }),
        )
    }

    public fetchMany(ids: string[]): Observable<SearchResponse<S>> {
        return this.searchAll(null, null, undefined, undefined, undefined, undefined, ids)
    }

    /**
     * This method uses the searchAll method with an different endpoint to use the old search way, useful to
     * retrieve paged data to keep in offline cache
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param options 
     */
    public synchronizeAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, options: DataStoreOptions = {}): Observable<SearchResponse<S>> {
        return this.searchAll(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, "/indexSearch/dbSearch", options);
    }

    /**
     * 
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param searchEndpointOverride 
     * @param options 
     * @param excludedIds 
     * @param onlyOnLocallyCreated 
     * @param forceSearchTextId When queryText value is an id (mongoDb), setting to true will include the entities referencing the id in addition to the entity corresponding to the id
     * @param language
     * @returns 
     */
    public searchAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, 
        searchEndpointOverride: string | null = null, options: DataStoreOptions = {}, excludedIds: string[] | null = null, onlyOnLocallyCreated: boolean = false, forceSearchTextId: boolean = false, language: string = FALLBACK_LOCALE): Observable<SearchResponse<S>> {

        // if online
        if (this.offlineDataFetcher && window.navigator.onLine) {
            return this.searchAllOnline(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, searchEndpointOverride, options, excludedIds, forceSearchTextId, language);
        } else {
            return this.searchAllOffline(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, searchEndpointOverride, options, excludedIds, onlyOnLocallyCreated, language);
        }
    }

    /**
     * 
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param searchEndpointOverride 
     * @param options 
     * @param excludedIds 
     * @param forceSearchTextId When queryText value is an id (mongoDb), setting to true will include the entities referencing the id in addition to the entity corresponding to the id
     * @param language
     * @returns 
     */
    public searchAllOnline(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {}, excludedIds: string[] | null = null, forceSearchTextId: boolean = false, language: string = FALLBACK_LOCALE)
        : Observable<SearchResponse<S>>  {
        
        let fetchId: boolean = false
        // allow search by id (search by id doesn't work in query text)
        if(queryText && queryText.match(MONGO_ID_REGEX)) {
            if(forceSearchTextId) {
                fetchId = true
            } else {
                filteredIds ? filteredIds.push(queryText) : filteredIds = [queryText]
                queryText = ''
            }
        }

        const endpoint = searchEndpointOverride || "/indexSearch/search";
        const searchTypes = types || this.typesForSearch // trying to get type name from endpoint (as they are suposed to be similar)
        const query = {
            queryText,
            sortObject,
            types: searchTypes,
            advancedSearches,
            excludedIds: excludedIds,
            filteredIds,
            group: this.userGroupId,
            language: language,
            page,
            pageSize,
            pathAndValueToCheck,
            searchOnArchived: false,
            searchOnVersioned: false
        }
        const cacheKey = `${endpoint}?${searchTypes.toString()}?${query.toString()}`;
        
        const defaultRequest$ = this.proxy.fetch(endpoint, undefined, 'POST', query).pipe(
            // tap(data => this.cache.write(cacheKey, data)),
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        )
        let request$ = defaultRequest$

        if(fetchId) {
            // include the entity with the given id in results
            request$ = this.fetchOne(queryText!).pipe(
                catchError(() => {
                    return defaultRequest$
                }),
                first(),
                switchMap(response => {
                    if(response && !isSearchResponse(response)) {
                        return zip(
                            of([response]),
                            defaultRequest$
                        )
                    }

                    return zip(of([]), defaultRequest$)
                }),
                switchMap(([resultFetchOne, resultDefaultRequest]) => {
                    let allData = resultDefaultRequest.allData ? resultFetchOne.concat(resultDefaultRequest.allData) : resultFetchOne
                    let results = resultDefaultRequest.results ? resultFetchOne.concat(resultDefaultRequest.results) : resultFetchOne

                    // remove eventual doubles
                    allData = _.uniqWith(allData, (entity1, entity2) => entity1.id === entity2.id)
                    results = _.uniqWith(results, (entity1, entity2) => entity1.id === entity2.id)

                    return of ({
                        totalSize: resultDefaultRequest.totalSize + resultFetchOne.length,
                        allData,
                        results,
                    })
                }),
            )
        }

        return request$.pipe(
            this.mergeUpdateCacheDataToSearchResults.bind(this),
            // Adding locally created entities to results
            switchMap( // TODO can be improved with sorting cached values and online values together
                async data => {
                    let localEntities = await this.getLocallyCreatedCachedObjectsAsS();
                    if (localEntities.length > 0) {
                        if (!data.allData) {
                            data.allData = []
                        }

                        // Restraining to filtered ids if there are some
                        if (filteredIds) {
                            const matchingLocalEntities = await this.offlineDataFetcher.searchAll(query, localEntities, await this.getLocallyUpdatedCachedObjectsAsS(), true).pipe(first()).toPromise();
                            localEntities = matchingLocalEntities.allData;
                        }

                        data.allData.push(...localEntities);
                        data.totalSize += localEntities.length;
                    }
                    return data
                }
            )
        );
    }

    public searchAllOffline(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {}, excludedIds: string[] | null = null, onlyOnLocallyCreated: boolean = false, language: string = FALLBACK_LOCALE)
        : Observable<SearchResponse<S>>  {
        const searchTypes = types || this.typesForSearch
        const query = {
            queryText,
            sortObject,
            types: searchTypes,

            advancedSearches,
            excludedIds: excludedIds,
            filteredIds,
            group: this.userGroupId,
            language: language,
            page,
            pageSize,
            pathAndValueToCheck,
            searchOnArchived: false,
            searchOnVersioned: false
        }

        return this.locallyCreatedEntitiesToData$()
                .pipe(
                    switchMap(
                        async data => {
                            const locallyUpdatedData = await this.getLocallyUpdatedCachedObjectsAsS();
                            return this.offlineDataFetcher.searchAll(query, data, locallyUpdatedData, onlyOnLocallyCreated, query.language);
                        }
                    ),
                    switchMap(
                        data => from(data)
                    ),
                    this.mergeUpdateCacheDataToSearchResults.bind(this)
                );
    }

    /**
     * Performs a simple text search  and returns the first result
     * Useful to search on exact item identifier for example
     * @param queryText 
     */
    public searchOneWithText(queryText: string, types: string[]): Promise<S> {
        return this.searchAll(queryText, null, undefined, types, 0, undefined, undefined, undefined, undefined, undefined).pipe(
            pluck('allData'),
            first(),
            map(
                searchResults => searchResults[0]
            )
        )
            .toPromise()
    }

    public searchTitles(types: string[], filterText: string, pageNumber: number = 0, defaultFilter: string = "", size: number = 10, filterIds?: string[], esPath?: any, language: string = FALLBACK_LOCALE) {
        if (window.navigator.onLine) {
            let searchOptions: any = {
                defaultFilter,
                esPath,
                filterText,
                language,
                pageNumber,
                size,
                types,
            }

            if (filterIds) {
                searchOptions = { ...searchOptions, filterIds }
            }
            return this.proxy.fetch("/indexSearch/title", searchOptions, "POST")
        } else {
            return from(this.offlineDataFetcher.searchTitles(types, filterText, pageNumber, defaultFilter, size, filterIds, esPath, language))
        }
    }

    public searchTitle(types: string[], id: string) {
        return this.searchTitles(
            types,
            "",
            0,
            undefined,
            1,
            [id]
        )
    }

    public notifyOn(pattern: RegExp) {
        return this.cache.notifyOn(pattern);
    }

    public updateOfflineMode(id: string, body: Partial<T>) {
        return this.fetchOneOfflineMode(id).pipe(
            tap(entity => {
                if(entity) {
                    body = this.mergeOverridingArrays(entity, body) as Partial<T>
                }
                (body as Partial<T> & {id: string}).id = id;
                // to allow sorting on updated date
                _.set(body, 'managementData.updatedDate', getCurrentDateAsSkinDate())
                this.offlineDataUpdateCacher.storeBeforePushing(id, body as Partial<T> & {id: string})
            }),
            switchMap(() => this.fetchOneOfflineMode(id))
        );
    }

    public mergeOverridingArrays(baseObject: Object, updates: Object) {
        const util = (baseValue, updatedValue) => {
            if(_.isArray(baseValue) && _.isArray(updatedValue)) {
                return updatedValue
            }
        }

        return _.mergeWith(baseObject, updates, util)
    }

    public update(id: string, body: Partial<T>, endpointOverride?: string, ignoreCachedStatus: boolean = false) {
        return from(this.hasCachedUpdates(id)).pipe(
            switchMap((hasCachedChanges: boolean) => {

                // If online, update directly in backend
                if (window.navigator.onLine && (!hasCachedChanges || ignoreCachedStatus)) { // differentiate locally created entities from other entities
                    // Online update
                    const endpoint = endpointOverride || this.endpoint;
                    const uri = `${endpoint}/${id}`;
                    if(_.get(body,'associatedMedias.medias', []) === null){
                        _.set(body,'associatedMedias.medias', [])
                    }
                    const localMedia = _.get(body,'associatedMedias.medias', []).filter(r => r.startsWith("local:"))
                    const uploadedMedia = _.get(body,'associatedMedias.medias', []).filter(r => !r.startsWith("local:"))
                    _.set(body,'associatedMedias.medias', uploadedMedia)
                    delete (body as any).associatedMedias.illustratedNote
                    return this.proxy
                        .fetch(uri, undefined, 'PUT', body, true)
                        .pipe(
                            switchMap(response => this.fetchOne(id)),
                            // Remove corresponding in updateCache (if exists) to avoid two versions of the entity
                            tap(async data => {
                                const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id)
                                if (cachedUpdatedValue) {
                                    this.offlineDataUpdateCacher.removeCachedValue(id)
                                }
                                localMedia.forEach(element => {
                                    _.get(body,'associatedMedias.medias', []).push(element)
                                });
                                // update cached data from fetch to avoid to versions of the entitiy
                                this.offlineDataFetcher.updateOne(id, data as T);
                            }),
                            switchMap(() => this.fetchOne(id, true)));
                }
                // If offline, store in cache and return cached data merged with updated data
                else {
                    return this.updateOfflineMode(id, body);
                }
                
            })
        )
    }

    public create(createBody?: Partial<T>, body?: Partial<T>): Observable<T> {
        const uri = `${this.endpoint}`;
        if (window.navigator.onLine || !body) {
            return this.proxy.fetch(uri, createBody, 'POST').pipe(
                concatMap(response => {
                    if (!body) {
                        return of(response);
                    }
                    // In case of a locally created entity, replacing temporary id by the one from db
                    const id: string | undefined = _.get(body, "id")
                    if (id && OfflineDataFetcher.isTemporaryId(id)) {
                        Object.defineProperty(body, "id", { value: response.id })
                    }
                    return this.update(response.id, lodashMerge(response, body));
                }),
                switchMap(({ id }) => this.fetchOne(id)),
            );
        } else { // Offline mode
            // the '_' prefix of the id identifies the id as temporary in the cache keys
            return this.createOffline(body);
        }
    }

    public createOffline(body: Partial<T>) {
        const tempId = '_' + Math.random().toString(36).substr(2, 9) + Math.random().toString(36).substr(2, 9);
        const newBody: Partial<T> & {id: string} = Object.assign({id: ''}, body);
        newBody.id = tempId;

        // to allow sort on creation and update date
        _.set(newBody, 'managementData.creationDate', getCurrentDateAsSkinDate())
        _.set(newBody, 'managementData.updatedDate', getCurrentDateAsSkinDate())

        return from(this.offlineDataUpdateCacher.storeBeforePushing(tempId, newBody)).pipe(
            switchMap(item => { 
                if (!item) {
                    // logger
                    throw new Error('Item not yet available')
                }
                return this.fetchOneOfflineMode(tempId) 
            }),
        )
    }

    public duplicate(entityToDuplicateId: string, createBody?: Partial<T>, body?: Partial<T>) {

        const duplicateUri = `${this.endpoint}/duplicate/${entityToDuplicateId}`;
        const getUri = `${this.endpoint}/${entityToDuplicateId}`

        return this.proxy.fetch(duplicateUri, createBody, 'POST').pipe(
            concatMap(response => {
                if (!body) {
                    return of(response);
                }
                return this.update(response.id, lodashMerge(response, body));
            }),
            switchMap(({ id }) => this.fetchOne(id))
        );
    }

    protected buildUploadCachedMediasReplaySubject(entityId: string, body: T, localMediasKeys: string[] | null): { [replayName: string]: ReplaySubject<ReplaySubjectContent> } {
        let mediaSubjects: { [replayName: string]: ReplaySubject<ReplaySubjectContent> } = {}
        
        if (localMediasKeys) {
            mediaSubjects[MEDIA_SUBJECTS_KEYS.image] = new ReplaySubject()
            const mediaTransferHelper = MediaTransferHelper.getInstance()
            
            const localImages = localMediasKeys.filter(mediaKey => mediaTransferHelper.getMediaType(mediaKey) === MediaType.IMAGE)
            
            const imageTransferHelper = ImageTransferHelper.getInstance()
            const mediaObservable = merge(from(localImages)).pipe(
                mergeMap((localImageKey) => {
                    return this.cache.read(localImageKey).pipe(
                        map(localImageValue => localImageValue as unknown as (string | File)),
                        map(localImageValue => {
                            if(localImageValue instanceof File) {
                                return localImageValue
                            } else {
                                return mediaTransferHelper.convertBase64ToFile(localImageValue, 'image/jpg')
                            }
                        }),
                        map(blob => imageTransferHelper.uploadImage('image/jpg', new File([blob], `temp.${blob.type.split('/').pop()}`), 'data:')),
                        map((cacheKeyUpload): ReplaySubjectContent => {
                            return {
                                cacheKeys: [cacheKeyUpload, localImageKey]
                            }
                        }),
                        catchError(err => of({
                            error: err
                        } as ReplaySubjectContent)),
                    )
                }),
                mergeMap(uploadRes => {
                    if(!uploadRes.error && uploadRes.cacheKeys) {
                        // we use the cacheKey from the upload as this is the one that will contain the uploaded media id
                        return imageTransferHelper.downloadImage(uploadRes.cacheKeys[0]).pipe(
                            map(response => {
                                let result: ReplaySubjectContent = {
                                    cacheKeys: uploadRes.cacheKeys,
                                }

                                if(response.newMediaId) {
                                    result.newMediaId = response.newMediaId
                                }
                                
                                return result
                            }),
                        )
                    }
                    
                    return of(uploadRes)
                }),
                filter(downloadRes => !!downloadRes.newMediaId),
                take(localImages.length),
            )

            mediaObservable.subscribe(mediaSubjects[MEDIA_SUBJECTS_KEYS.image])
        }

        return mediaSubjects
    }

    /**
     * 
     * @param body Is updated during the execution of this method
     * @param localAssociatedMedias Is updated during the execution of this method
     * @returns An updated version (copy) of localMedias
     */
    private async handleLocalPrincipalsMedias(id: string, body: T & { associatedMedias: AssociatedMedias }, localMedias: string[], localAssociatedMedias: AssociatedMedias) {
        let oldAssociatedMedias: AssociatedMedias | null = null
        if(!OfflineDataFetcher.isTemporaryId(id)) {
            let entityBeforeUpdate: T | null = null
            try{
                entityBeforeUpdate = await this.fetchOneOnlineMode(id).toPromise()
            } catch(err) {
                entityBeforeUpdate = null
            }
            oldAssociatedMedias = (entityBeforeUpdate as any)?.associatedMedias ?? null
        }

        const principalMediaKeys = ['first', 'second', 'third']
        for(const key of principalMediaKeys) {
            if(body.associatedMedias[key] && body.associatedMedias[key].startsWith('local:')) {
                localMedias.unshift(body.associatedMedias[key])

                localAssociatedMedias[key] = body.associatedMedias[key]

                // replacing principal media with old principal media to avoid modifying entity with an illegal state
                if(oldAssociatedMedias && oldAssociatedMedias[key]) {
                    body.associatedMedias[key] = oldAssociatedMedias[key]
                } else {
                    body.associatedMedias[key] = undefined
                }
            }
        }

        return _.uniq(localMedias)
    }

    /**
     * Returns an observable that emits a boolean value for 
     * each push of an entity in the cache (true for success, false for failure)
     */
    public async handleSynchroUpdateFromCache(pushNewEntities: boolean = true): Promise<Observable<boolean>> {
        this.globalPushing = true;
        let successCounter = 0;
        // getting ids of entities in cache)
        let allIds: string[] = await this.offlineDataUpdateCacher.getCachedObjectsKeys();
        // Remove new entities if necessary
        if (!pushNewEntities) {
            allIds = allIds.filter(id => !id.includes('_'))
        }
        const idsObs = of(allIds);
        return idsObs.pipe(
            // Retrieving data to update from cache
            switchMap(
                async ids => {
                    const dataToUpdate: Array<SynchroMediaUpdates<T>> = [];
                    for (const id of ids) {
                        // Check if pushing has been interrupted
                        if (this.globalPushing) {
                            const sanitizedId = this.offlineDataUpdateCacher.getSanitizedId(id);
                            const body = await this.offlineDataUpdateCacher.getCachedValue(sanitizedId);

                            // Cleaning unwanted undefined cached values
                            if (sanitizedId === "undefined") {
                                await this.offlineDataUpdateCacher.removeCachedValue(sanitizedId);
                            }

                            const fixSubelement = ((item: { [key: string]: any | any[] }) => {
                                for (const [key, value] of Object.entries(item)) {
                                    if (typeof value === 'object' && _.isEmpty(value)) {
                                        _.set(item, key, null)
                                    } else if (Array.isArray(value)) {
                                        value.forEach(e => fixSubelement(e))
                                        _.set(item, key, value.filter(o => !(typeof o === 'object' && o !== null && _.isEmpty(o))))
                                    } else if (typeof value === 'object' && value !== null) {
                                        fixSubelement(value)
                                    }
                                }
                            })
                    
                            fixSubelement(body as unknown as {[key: string]:any})

                            let localMedias: string[] | null = null
                            let localAssociatedMedias: AssociatedMedias = {}
                            if(this.hasAssociatedMedias(body)) {
                                localMedias = _.get(body.associatedMedias, 'medias', []).filter(m => m.startsWith('local'))
                                localAssociatedMedias.medias = localMedias ?? undefined // assign undefined in case localMedias == null
                                _.remove(_.get(body.associatedMedias, 'medias', []), (m: string) => m.startsWith('local'))

                                // managing first medias (first, second, ...) to prevent pushing local media keys to the API
                                localMedias = await this.handleLocalPrincipalsMedias(sanitizedId, body, localMedias, localAssociatedMedias)
                            }

                            const replaySubjects = this.buildUploadCachedMediasReplaySubject(sanitizedId, body, localMedias)

                            dataToUpdate.push({ id: sanitizedId, body, mediaSubjects: replaySubjects, cachedMedias: localAssociatedMedias });
                        }
                    }
                    // When there are only locally created entities and 'pushNewEntities' is false, we have to return something
                    // to notify that the push is finished eventhough nothing has been pushed directly (ex : auditRecords)
                    if (!pushNewEntities && dataToUpdate.length === 0) {
                        return {} as any
                    }
                    return of(...dataToUpdate); // quand [] = :'(
                }
            ),
            // this switchMap is required to cancel the async of the previous callback, which
            // generates a Observable instead of the wanted data directly
            switchMap(data => data),
            tap(() => {
                successCounter++
            }),
            // Performing updates
            concatMap(
                (e: any) => {
                    if(e && e.id && OfflineDataFetcher.isTemporaryId(e.id)) {
                        return this.pushCachedCreations(e).pipe(
                            switchMap((res: PushCachedValueResult) => {
                                if(res.success) {
                                    this.offlineDataUpdateCacher.removeCachedValue(e.id)
                                    if(res.newId) {
                                        this.offlineDataUpdateCacher.removeCachedValue(res.newId)
                                    }
                                }
                                return of(res.success)
                            })
                        )
                    }
                    else {
                        return this.pushCachedUpdate(e).pipe(
                            switchMap((res: PushCachedValueResult) => {
                                if(res.success) {
                                    this.offlineDataUpdateCacher.removeCachedValue(e.id)
                                }
                                return of(res.success)
                            })
                        )
                    } 
                } 
            ),
        )
    }

    protected pushCachedUpdate(dataToUpdate: SynchroMediaUpdates<T>): Observable<PushCachedValueResult> {
        if (dataToUpdate.id && dataToUpdate.body) {
            // Delete updated date (used locally but has to be set by the api)
            // @ts-ignore
            delete dataToUpdate.body.managementData?.updatedDate

            this.synchronizationMap[dataToUpdate.id] = 'inProgress'
            return this.update(
                dataToUpdate.id,
                dataToUpdate.body,
                undefined,
                true
            )
                .pipe(
                    first(),
                    // Handle success or error if the update and emit boolean
                    catchError(e => of(Error('Error during update...'))),
                    switchMap(
                        response => {
                            if (!(response instanceof Error)) {
                                if (OfflineDataFetcher.isTemporaryId(dataToUpdate.id)) {
                                    this.synchronizationMap[dataToUpdate.id] = (response as unknown as {id: string}).id
                                }
                                return of({
                                    success: true
                                })
                            }
                            return of({
                                success: false
                            });
                        }
                    )
                )
        }
        else {
            return of({
                success: false
            });
        }
    }

    protected pushCachedCreations(dataToCreate: SynchroMediaUpdates<T>): Observable<PushCachedValueResult> {
        if (dataToCreate.id && dataToCreate.body) {
            // Delete management data dates (has to be set by the API on the official creation of the item)
            // @ts-ignore
            delete dataToCreate.body.managementData?.creationDate
            // @ts-ignore
            delete dataToCreate.body.managementData?.updatedDate

            this.synchronizationMap[dataToCreate.id] = 'inProgress'
            return this.create(
                undefined,
                dataToCreate.body
            )
                .pipe(
                    first(),
                    // Handle success or error if the update and emit boolean
                    catchError(e => of(Error('Error during update...'))),
                    switchMap(
                        response => {
                            if (!(response instanceof Error)) {
                                const newId = (response as unknown as {id: string}).id
                                this.synchronizationMap[dataToCreate.id] = newId
                                return of({
                                    success: true,
                                    newId: newId
                                })
                            }
                            return of({
                                success: false
                            });
                        }
                    )
                )
        }
        else {
            return of({
                success: true
            });
        }
    }

    public hasAssociatedMedias(item): item is {associatedMedias: AssociatedMedias} {
        return !!item.associatedMedias?.medias
    }

    public async getNbCachedObjects(countLocallyCreated: boolean = true) {
        return await this.offlineDataUpdateCacher.getNbCachedObjects(countLocallyCreated);
    }

    public async getObjectInUpdateCache(id: string) {
        return this.offlineDataUpdateCacher.getCachedValue(id)
    }

    public async hasCachedUpdates(id: string): Promise<boolean> {
        const res = await this.getObjectInUpdateCache(id) != null
        return res
    }

    public async removeObjectFromUpdateCache(id: string) {
        return this.offlineDataUpdateCacher.removeCachedValue(id)
    }

    public deleteModifications(id: string) {
        return from(this.offlineDataUpdateCacher.fetchOneFromId(id)).pipe(map(e => {
            _.get((e), 'associatedMedias.medias', [])
                .filter(media => media.startsWith('local'))
                .forEach(media => {
                    switch(MediaTransferHelper.getInstance().getMediaType(media)) {
                        case MediaType.IMAGE:
                            ImageTransferHelper.getInstance().deleteImageInCache(media)
                            break;
                    }
                })

            return this.offlineDataUpdateCacher.deleteBeforePushing(id);
        }))
    }

    public delete(id: string) {
        if (OfflineDataFetcher.isTemporaryId(id)) {
            return this.deleteModifications(id);
        }
        const uri = `${this.endpoint}/${id}`;

        const network$ = this.proxy.fetch(uri, undefined, "DELETE", undefined, undefined).pipe(
            last(),
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        );
        return network$.pipe(distinctUntilChanged(isEqual));
    }

    public async synchronize(pageIndex: number, clearBefore?: boolean, firstCall: boolean = true) {
        if (firstCall) {
            this.executionStartTime = DateTime.now();
            this.abortSync = false;
            this.currentNbSychronized = 0;
            this.syncing = true;
            this.saveStats(DataSynchronisationStatus.SUCCESS);
        }
        if (!this.abortSync) {
            // Freeing last subscription
            if (this.currentSynchroSubscription) {
                this.currentSynchroSubscription.unsubscribe()
            }

            if (clearBefore) {
                await this.offlineDataFetcher.clear()
                this.currentNbSychronized = 0;
            }

            this.currentSynchroSubscription =
                iif(() => window.navigator.onLine,
                    this.fetchPage(pageIndex),
                    throwError("Synchronization cannot be performed in offline mode")
                )
                    .pipe(
                        first(),
                        catchError(
                            (error) => {
                                this.saveStats(DataSynchronisationStatus.ERROR);
                                this.syncing = false;
                                dataSynchroEventBus$.next({
                                    name: this.mainCacheKey,
                                    status: DataSynchronisationStatus.ERROR,
                                })
                                throw error;
                            }
                        )
                    )
                    .subscribe(
                        async searchResponse => {

                            // Retrieving total number of results for stats
                            if (this.maxNbToSynchronize === Infinity) {
                                this.maxNbToSynchronize = searchResponse.totalSize
                            }

                            // Appending results if there are ones, ending syncing otherwise
                            if (searchResponse.results.length > 0) {
                                const dataToCache = this.formatDataForCaching(searchResponse.results);
                                await this.offlineDataFetcher.append(dataToCache)
                                if (this.emulateSearch) {
                                    this.currentNbSychronized += 1;
                                } else {
                                    this.currentNbSychronized += searchResponse.results.length;
                                }
                                this.saveStats(DataSynchronisationStatus.SUCCESS);
                                this.synchronize(pageIndex++, false, false)
                            } else {
                                dataSynchroEventBus$.next({
                                    name: this.mainCacheKey,
                                    status: DataSynchronisationStatus.SUCCESS,
                                })
                                this.saveStats(DataSynchronisationStatus.SUCCESS);
                                this.syncing = false;
                            }
                        }
                    )
            pageIndex++;
        }
        else {
            this.saveStats(DataSynchronisationStatus.ABORTED);

            this.abortSync = false;
            this.syncing = false;
            dataSynchroEventBus$.next({
                name: this.mainCacheKey,
                status: DataSynchronisationStatus.ABORTED,
            })
            this.saveStats(DataSynchronisationStatus.ABORTED)
        }
    }

    /**
     * While caching (pull synchro), used for convert fetched data into the same format returned while using API.
     */
    public formatDataForCaching(rawData) {
        return rawData
    }

    public abortPush() {
        this.globalPushing = false;
    }

    public abortSynchronisation() {
        this.abortSync = true;
    }

    public async fetchNbEntitiesInUpdateCache() {
        this.nbEntitiesInUpdateCache = await this.getNbCachedObjects(true)
        this.nbCreatedEntitiesInUpdateCache = this.nbEntitiesInUpdateCache - await this.getNbCachedObjects(false)
    }

    public fetchOneFromPredicate(predicate: (data: T) => boolean) {
        if (window.navigator.onLine) {
            throw new Error("This method is not meant to be called in an online context");
        } else {
            return this.offlineDataFetcher.fetchOneFromPredicate(predicate)
        }
    }

    public fetchAllFromPredicate(predicate: (data: T) => boolean) {
        if (window.navigator.onLine) {
            throw new Error("This method is not meant to be called in an online context");
        } else {
            return this.offlineDataFetcher.fetchAllFromPredicate(predicate)
        }
    }

    public recursiveGenericMerge(base, newFields) {
        if (!base) {
            return newFields
        }
        if (!newFields) {
            return base
        }
        if (typeof base !== 'object' || typeof newFields !== 'object') {
            return newFields || base
        }
        let keys = Object.keys(newFields)
        Object.keys(base).forEach((e) => keys.push(e))
        keys = _.uniq(keys)
        const mergeResult = {}
        for (const field of keys) {
            if ((base[field] && Array.isArray(base[field])) || (newFields[field] && Array.isArray(newFields[field]))) {
                mergeResult[field] = _.zip(base[field], newFields[field]).map(e => this.recursiveGenericMerge(...e))
            } else if ((base[field] && typeof base[field] === 'object') || (newFields[field] && typeof newFields[field] === 'object')) {
                mergeResult[field] = this.recursiveGenericMerge(base[field], newFields[field])
            } else {
                mergeResult[field] = newFields[field] || base[field]
            }
        }
        return mergeResult
    }

    public emptySynchronizationMap(): void {
        this.synchronizationMap = {}
    }

    public getUpdatedValue(localId: string): string | undefined {
        return this.synchronizationMap[localId]
    }

    public addImage(element, image) {
        this.proxy.fetch(`${this.endpoint}/addMedia/${element.id}`,{idMedia: image.newMediaId}, 'PUT')
    }

    // tslint:disable-next-line:member-ordering
    protected convertTtoS(toConvert: T): S {
        return toConvert as any as S;
    }

    // tslint:disable-next-line:member-ordering
    protected convertStoT(toConvert: S): T {
        return toConvert as any as T;
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new OfflineDataFetcher<T, S>(this.sanitizedIdentifer);
    }

    protected initializeDataPusher() {
        this.offlineDataUpdateCacher = new OfflineDataUpdateCacher<T>(this.sanitizedIdentifer);
    }

    protected mergeUpdateCacheDataToOneResult(source$: Observable<T>) {
        return source$.pipe(switchMap(
            // retrieving cache data that has not been pushed to keep the vue up to date
            async (data: any) => {
                const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(data.id);
                if (cachedUpdatedValue) {
                    data = lodashMerge(data, cachedUpdatedValue)
                }
                return data
            }
        ))
    }

    /**
     * Returns an the locally created entities as an array
     */
    private async getLocallyCreatedCachedObjectsAsS(): Promise<S[]> {
        return (await this.offlineDataUpdateCacher.getLocallyCreatedCachedObjects()).map(this.convertTtoS)
    }

    private async getLocallyUpdatedCachedObjectsAsS(): Promise<S[]> {
        return (await this.offlineDataUpdateCacher.getLocallyUpdatedCachedObjects()).map(this.convertTtoS)
    }

    /**
     * Returns an observable emitting the locally created entities as an array
     */
    private locallyCreatedEntitiesToData$(): Observable<S[]> {
        return from(this.getLocallyCreatedCachedObjectsAsS())
    }

    private saveStats(status: DataSynchronisationStatus) {
        this.offlineDataFetcher.saveStats({
            currentNbSychronized: this.currentNbSychronized,
            lastSynchroDate: DateTime.now().toMillis(),
            lastSynchroDuration: this.executionStartTime ? this.executionStartTime.diffNow().toMillis() : 0,
            lastSynchroStatus: status,
            maxNbToSynchronize: this.maxNbToSynchronize,
        });
    }

    /**
     * Pipeable operator thats merges data in the update cache that have not been pushed yet
     * into the search result (either from sync-cache or directly network result)
     * Only for results of a search request
     * @param source$ 
     */
    private mergeUpdateCacheDataToSearchResults(source$: Observable<SearchResponse<S>>) {
        return source$.pipe(switchMap(
            // retrieving cache data that has not been pushed to keep the vue up to date
            async (data: SearchResponse<S>) => {
                const results = data.allData;
                if (results) {
                    for (let result of results) {
                        const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(_.get(result, 'id'));
                        if (cachedUpdatedValue) {
                            result = this.mergeCacheToEntity(result, cachedUpdatedValue)
                        }
                    }
                }
                return data
            }
        ))
    }

    private mergeCacheToEntity(entity: any, cachedValue: any) {
        const cachedCurrentState = _.get(cachedValue, 'workflowSchema.currentState')
        const mergedResult = lodashMerge(entity, cachedValue)

        if (cachedCurrentState) {
            _.set(mergedResult, 'workflowSchema.currentState', cachedCurrentState);
        }

        return mergedResult;
    }

    private cache$(key: string): Observable<T[]> {
        return this.cache.read(key).pipe(filter(data => data !== undefined)) as Observable<T[]>;
    }

    private fetchPage(pageIndex: number): Observable<SearchResponse<S>> {
        return this.synchronizeAll(
            "",
            null,
            SEARCH_PAGE_SIZE,
            this.typesForSyncDown,
            pageIndex,
            undefined,
            undefined,
            undefined,
            { emulateSearch: this.emulateSearch }
        )
    }

    public async clearCacheDown() {
        return this.offlineDataFetcher.clear()
    }
}
